import React from "react";
import AppModal from "components/modal/AppModal";
import { emissionProfilesApi } from "api/endpoints";

const INPUT_BASE =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 transition focus:border-brand-500 focus:outline-none dark:border-white/15 dark:bg-navy-900 dark:text-white";
const BTN_PRIMARY =
  "rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300";
const BTN_SECONDARY =
  "rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-navy-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/20 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800";

function saveBlob(blob, filename) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || "certificado.pfx";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

function errorMessage(data, fallback) {
  return (
    data?.error ||
    data?.message ||
    data?.detail ||
    fallback
  );
}

/**
 * Descarga verificada del certificado digital (solo admin).
 * Flujo en 2 pasos, igual que el login: 1) se envía un código al correo del
 * admin, 2) se ingresa el código y recién ahí se entrega el archivo .pfx.
 */
export default function CertificateDownloadModal({ isOpen, onClose, profile }) {
  const [step, setStep] = React.useState("intro"); // "intro" | "code"
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [challengeId, setChallengeId] = React.useState("");
  const [emailHint, setEmailHint] = React.useState("");
  const [code, setCode] = React.useState("");
  const [resendIn, setResendIn] = React.useState(0);

  const profileId = profile?.id ?? profile?.profile_uuid ?? null;

  const reset = React.useCallback(() => {
    setStep("intro");
    setLoading(false);
    setError("");
    setNotice("");
    setChallengeId("");
    setEmailHint("");
    setCode("");
    setResendIn(0);
  }, []);

  // Al cerrar, limpiar todo (no dejar código/challenge colgando).
  React.useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  // Cuenta regresiva del reenvío.
  React.useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const startCooldown = React.useCallback((resendAvailableAt) => {
    if (!resendAvailableAt) {
      setResendIn(30);
      return;
    }
    const secs = Math.max(0, Math.ceil((new Date(resendAvailableAt).getTime() - Date.now()) / 1000));
    setResendIn(secs || 30);
  }, []);

  async function handleRequestCode() {
    if (!profileId) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const { res, data } = await emissionProfilesApi.requestCertificateDownloadCode(profileId);
      if (!res.ok) {
        setError(errorMessage(data, "No se pudo enviar el código. Intenta nuevamente."));
        return;
      }
      const challenge = data?.challenge || {};
      setChallengeId(challenge.challenge_id || "");
      setEmailHint(challenge.email_hint || "");
      startCooldown(challenge.resend_available_at);
      setStep("code");
      setNotice("Te enviamos un código a tu correo. Ingrésalo para descargar el certificado.");
    } catch (e) {
      setError("No se pudo enviar el código. Revisa tu conexión e intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!profileId || resendIn > 0) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const { res, data } = await emissionProfilesApi.resendCertificateDownloadCode(profileId, challengeId);
      if (!res.ok) {
        setError(errorMessage(data, "No se pudo reenviar el código."));
        return;
      }
      const challenge = data?.challenge || {};
      if (challenge.challenge_id) setChallengeId(challenge.challenge_id);
      if (challenge.email_hint) setEmailHint(challenge.email_hint);
      startCooldown(challenge.resend_available_at);
      setNotice("Te reenviamos un nuevo código a tu correo.");
    } catch (e) {
      setError("No se pudo reenviar el código.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload() {
    if (!profileId || code.trim().length < 4) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const { res, blob, data, filename } = await emissionProfilesApi.downloadCertificate(profileId, {
        challengeId,
        code: code.trim(),
      });
      if (!res.ok || !blob) {
        setError(errorMessage(data, "El código es incorrecto o expiró. Solicita uno nuevo."));
        return;
      }
      saveBlob(blob, filename || "certificado.pfx");
      onClose?.();
    } catch (e) {
      setError("No se pudo descargar el certificado. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  }

  const footer =
    step === "intro" ? (
      <>
        <button type="button" className={BTN_SECONDARY} onClick={onClose} disabled={loading}>
          Cancelar
        </button>
        <button type="button" className={BTN_PRIMARY} onClick={handleRequestCode} disabled={loading || !profileId}>
          {loading ? "Enviando código…" : "Enviar código al correo"}
        </button>
      </>
    ) : (
      <>
        <button type="button" className={BTN_SECONDARY} onClick={() => setStep("intro")} disabled={loading}>
          ← Volver
        </button>
        <button
          type="button"
          className={BTN_PRIMARY}
          onClick={handleDownload}
          disabled={loading || code.trim().length < 4}
        >
          {loading ? "Verificando…" : "Descargar certificado"}
        </button>
      </>
    );

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title="Descargar certificado digital"
      footer={footer}
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-200">
          Por seguridad, el certificado digital es información sensible. Para
          descargarlo debes confirmar tu identidad con un código que enviaremos a
          tu correo.
        </div>

        {profile ? (
          <div className="text-sm text-navy-700 dark:text-gray-200">
            <div className="font-semibold">
              {profile.profile_name || profile.nombre_firmante || "Certificado"}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              RUT firmante: {profile.rut_firmante || profile.rut_emisor || "—"}
            </div>
          </div>
        ) : null}

        {step === "code" ? (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
              Código de verificación
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6 dígitos"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleDownload();
              }}
              className={INPUT_BASE}
              autoFocus
            />
            {emailHint ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enviado a {emailHint}
              </p>
            ) : null}
            <button
              type="button"
              onClick={handleResend}
              disabled={loading || resendIn > 0}
              className="mt-2 text-xs font-semibold text-brand-500 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resendIn > 0 ? `Reenviar código (${resendIn}s)` : "Reenviar código"}
            </button>
          </div>
        ) : null}

        {notice ? (
          <div className="rounded-lg bg-green-50 px-3 py-2 text-xs text-green-700 dark:bg-green-400/10 dark:text-green-300">
            {notice}
          </div>
        ) : null}
        {error ? (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-400/10 dark:text-red-300">
            {error}
          </div>
        ) : null}
      </div>
    </AppModal>
  );
}
